// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import client from '@Client';
import { THLayout } from '@Molecules/THLayout';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { ActionOutput, AlertOutput, AuditOutput, ObjectTypeV1 as ObjectType, OrgPermissionsEnum } from 'thehive-sdk';
import useConfigList from '@Hooks/useConfigList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { useLocalMessage } from '@Helpers/locale';
import ResponderReports from '@Organisms/Report/ResponderReports/ResponderReports';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { ICONS } from '@Constants/iconsApp';
import { ResponderDrawer } from '@Organisms/Responder';
import { Space } from 'antd';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import useUserPermissions from '@Hooks/useUserPermissions';
import usePageTitle from '@Hooks/usePageTitle';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector } from '@Organisms/Alert/selector';

import './Responders.scss';

const actionIsAlert = (audit: AuditOutput): boolean => audit.base?.details?.objectType === 'Alert';

const AlertResponders = (): ReactElement => {
    const i = useLocalMessage();

    const { alertId } = useParams<{ alertId: string }>();

    const hasPermissions = useUserPermissions({ permissionsNeeded: [OrgPermissionsEnum.MANAGE_ACTION] });

    const config = useConfigList<ActionOutput>({
        selector: {
            head: [{ name: 'getAlert', idOrName: alertId }, { name: 'actions' }],
            name: `get-report-alert-${alertId}`,
            sort: ['-startDate'],
        },
        controller: client.AlertController,
        storageId: StorageFilterEnum.ALERT_RESPONDERS,
        objectType: ObjectType.ACTION,
        pagination: 'on',
        streamRootId: alertId,
    });

    const { dataSource = [], listRefetch, status = 'loading', columnsRenderer } = config;

    useStreamObserver({ objectType: STREAM_EVENTS.ACTION }, (audits: AuditOutput[]): void => {
        if (audits.some(actionIsAlert)) {
            listRefetch();
        }
    });

    const { data: alertData } = useOneQuery<AlertOutput>({
        selector: computeGetSelector({ alertId }),
        controller: client.AlertController,
        manual: true,
    });

    usePageTitle(i('page-title.alerts.responders', { title: alertData?.title || '' }));

    return (
        <THLayout>
            <THLayout.Content>
                {dataSource.length ? (
                    <ResponderReports reports={dataSource} columnsRenderer={columnsRenderer} />
                ) : (
                    <DisplayEmptyData
                        spin={status === 'loading'}
                        label={
                            <Space size={2}>
                                {i('responder.no-reports')}
                                {hasPermissions && (
                                    <ResponderDrawer
                                        triggerElem={<THButton type="link"> {i('responder.launch')}</THButton>}
                                        objectId={alertId}
                                        objectType="alert"
                                    />
                                )}
                            </Space>
                        }
                    />
                )}
            </THLayout.Content>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'alert.layout.nav-bar.responders',
        icon: <ICONS.RespondersIcon />,
    },
    path: RouteBuilder.compute('alerts.responders'),
    content: <AlertResponders />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.alert.responders' },
};
